import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCipherStore } from '../store/cipherStore';
import { exportAsText, analyzeText } from '../utils';

const CipherOutput = () => {
  const { outputText, swapInputOutput } = useCipherStore();
  const [copied, setCopied] = useState(false);
  
  const textAnalysis = analyzeText(outputText);
  
  const handleCopy = async () => {
    if (outputText) {
      try {
        await navigator.clipboard.writeText(outputText);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (error) {
        console.error('Failed to copy text:', error);
      }
    }
  };
  
  const handleExport = () => {
    if (outputText) {
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      exportAsText(outputText, `cipher-result-${timestamp}.txt`);
    }
  };
  
  const handleSwap = () => {
    swapInputOutput();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      className="cyber-border rounded-lg p-6 space-y-4"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-neon-green glow-text">Output Text</h2>
        <div className="text-sm text-gray-400">
          {textAnalysis.characters} chars | {textAnalysis.words} words
        </div>
      </div>
      
      <div className="relative">
        <textarea
          value={outputText}
          readOnly
          placeholder="Encrypted/decrypted text will appear here..."
          className="cyber-input w-full h-32 p-4 rounded-lg resize-none scrollbar-cyber bg-gray-900/90"
        />
        
        <AnimatePresence>
          {outputText && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="absolute top-2 right-2 flex gap-1"
            >
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={handleCopy}
                className="p-2 bg-gray-800/80 hover:bg-gray-700/80 rounded-lg text-neon-cyan transition-colors"
                title="Copy to clipboard"
              >
                {copied ? '✓' : '📋'}
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      <div className="flex flex-wrap gap-3">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleSwap}
          disabled={!outputText}
          className="cyber-button rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          🔄 Use as Input
        </motion.button>
        
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleExport}
          disabled={!outputText}
          className="cyber-button rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          💾 Export
        </motion.button>
        
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleCopy}
          disabled={!outputText}
          className="cyber-button rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {copied ? '✓ Copied!' : '📋 Copy'}
        </motion.button>
      </div>
      
      {outputText && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="text-xs text-gray-500 bg-gray-800/50 p-3 rounded-lg"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div>Length: {textAnalysis.length}</div>
            <div>Words: {textAnalysis.words}</div>
            <div>Lines: {textAnalysis.lines}</div>
            <div>Entropy: {textAnalysis.entropy}</div>
          </div>
        </motion.div>
      )}
      
      <AnimatePresence>
        {copied && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="text-center text-neon-green text-sm"
          >
            Text copied to clipboard!
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default CipherOutput;
